// src/components/report-forms/InformeImagenesForm.js
import React, { useRef } from 'react';
import { PlusCircleIcon, TrashIcon } from '@heroicons/react/24/outline';
import imageCompression from 'browser-image-compression';

const InformeImagenesForm = ({ formData, setFormData, photoPreviews, setPhotoPreviews, setNotification }) => {

    const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));
    const fileInputRef = useRef(null);

    // Fotos con título y descripción individual
    const updatePhotoMeta = (index, field, value) => {
        setPhotoPreviews(prev => {
            const updated = [...prev];
            updated[index] = { ...updated[index], [field]: value };
            return updated;
        });
    };

    const handlePhotoChange = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;
        setNotification({ message: 'Procesando imágenes...', type: 'info' });
        try {
            const compressed = await Promise.all(files.map(f => imageCompression(f, { maxSizeMB: 0.8, maxWidthOrHeight: 1280 })));
            const newPhotos = compressed.map(f => ({
                src: URL.createObjectURL(f),
                name: f.name,
                file: f,
                caption: '',
                description: ''
            }));
            setPhotoPreviews(prev => [...prev, ...newPhotos]);
            setNotification({ message: `${files.length} imagen(es) agregada(s).`, type: 'success' });
        } catch (err) {
            console.error(err);
            setNotification({ message: 'Error al procesar imágenes.', type: 'error' });
        }
        e.target.value = '';
    };

    const removePhoto = (index) => {
        setPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="space-y-6">
            {/* Datos del Proyecto */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">I. Datos del Proyecto</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium dark:text-gray-300">Título del Informe *</label>
                        <input type="text" value={formData.titulo || ''} onChange={e => updateField('titulo', e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" required placeholder="Ej: Informe de Imágenes - Instalación Eléctrica Bodega" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium dark:text-gray-300">Ubicación / Dirección</label>
                        <input type="text" value={formData.ubicacion || ''} onChange={e => updateField('ubicacion', e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                    </div>
                    <div>
                        <label className="block text-sm font-medium dark:text-gray-300">Fecha</label>
                        <input type="date" value={formData.fecha || new Date().toISOString().split('T')[0]} onChange={e => updateField('fecha', e.target.value)} className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
                    </div>
                </div>
            </div>

            {/* Descripción General */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">II. Descripción General</h2>
                <textarea value={formData.descripcionGeneral || ''} onChange={e => updateField('descripcionGeneral', e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Describa brevemente el contexto del registro fotográfico..." />
            </div>

            {/* Registro Fotográfico con Descripción */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">III. Registro Fotográfico</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Cada imagen incluirá un título referencial y una descripción detallada en el informe PDF.</p>

                <button type="button" onClick={() => fileInputRef.current.click()} className="bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-4 py-2 rounded-lg font-semibold mb-6 border dark:border-green-800 flex items-center gap-2 hover:shadow transition">
                    <PlusCircleIcon className="w-5 h-5" /> Agregar Imágenes
                </button>
                <input type="file" ref={fileInputRef} onChange={handlePhotoChange} multiple accept="image/*" className="hidden" />

                {photoPreviews.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed dark:border-gray-600 rounded-xl text-gray-400 dark:text-gray-500">
                        <p className="text-lg">📷 No hay imágenes aún</p>
                        <p className="text-sm mt-1">Presione el botón verde para agregar fotos</p>
                    </div>
                ) : (
                    <div className="space-y-4">
                        {photoPreviews.map((photo, index) => (
                            <div key={index} className="border dark:border-gray-600 rounded-xl p-4 bg-gray-50 dark:bg-gray-700/50 flex flex-col md:flex-row gap-4 group">
                                <div className="relative shrink-0">
                                    <img src={photo.src} alt={`Foto ${index + 1}`} className="w-full md:w-48 h-36 object-cover rounded-lg border dark:border-gray-600 shadow" />
                                    <button type="button" onClick={() => removePhoto(index)} className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition shadow">
                                        <TrashIcon className="w-4 h-4" />
                                    </button>
                                    <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] px-2 py-0.5 rounded-full font-bold">Foto {index + 1}</span>
                                </div>
                                <div className="flex-1 space-y-2">
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Título / Referencia</label>
                                        <input
                                            type="text"
                                            value={photo.caption || ''}
                                            onChange={e => updatePhotoMeta(index, 'caption', e.target.value)}
                                            className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                                            placeholder="Ej: Vista general del tablero principal"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400">Descripción Detallada</label>
                                        <textarea
                                            value={photo.description || ''}
                                            onChange={e => updatePhotoMeta(index, 'description', e.target.value)}
                                            rows="2"
                                            className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 text-sm"
                                            placeholder="Describa lo que se observa en la imagen..."
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Conclusiones y Recomendaciones */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">IV. Conclusiones y Recomendaciones</h2>
                <div className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium dark:text-gray-300">Conclusiones</label>
                        <textarea value={formData.conclusiones || ''} onChange={e => updateField('conclusiones', e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Conclusión general del informe fotográfico..." />
                    </div>
                    <div>
                        <label className="block text-sm font-medium dark:text-gray-300">Recomendaciones</label>
                        <textarea value={formData.recomendaciones || ''} onChange={e => updateField('recomendaciones', e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Recomendaciones técnicas..." />
                    </div>
                </div>
            </div>

            {/* Observaciones Finales */}
            <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-transparent dark:border-gray-700">
                <h2 className="text-xl font-semibold mb-4 border-b dark:border-gray-700 pb-2 text-gray-700 dark:text-gray-200">V. Observaciones Finales</h2>
                <textarea value={formData.observaciones || ''} onChange={e => updateField('observaciones', e.target.value)} rows="3" className="w-full border dark:border-gray-600 rounded p-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" placeholder="Observaciones complementarias al registro fotográfico..." />
            </div>
        </div>
    );
};

export default InformeImagenesForm;
